import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { CloseOutlinedIcon } from 'icons/materialIcons';

/**
 * Reusable bottom selection bar component
 * Shows selection count and action button at the bottom of the page
 * 
 * @param {boolean} open - Whether the bar is visible
 * @param {number} selectedCount - Number of selected items
 * @param {string} itemName - Name of the item type (e.g., "Subteam", "Member", "Lead")
 * @param {string} actionText - Text for the action button (e.g., "Assign Restrictions")
 * @param {function} onAction - Callback when action button is clicked
 * @param {function} onClose - Callback when close button is clicked
 */
const TeamSelectionBottomBar = ({
    open = false,
    selectedCount = 0,
    itemName = 'Item',
    actionText = 'Assign Restrictions',
    onAction,
    onClose
}) => {
    if (!open || selectedCount === 0) {
        return null;
    }

    const handleActionClick = () => {
        if (onAction) {
            onAction();
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    // Format the selection text
    const selectionText = selectedCount === 1
        ? `1 ${itemName} selected`
        : `${selectedCount} ${itemName}s selected`;

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 0,
                left: { xs: 0, sm: '224px' }, // Sidebar (208px = 13rem) + container left padding (16px)
                right: { xs: 0, sm: '16px' }, // Container right padding (16px = 2rem padding)
                backgroundColor: '#000',
                color: '#fff',
                padding: '16px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                zIndex: 1300, // Higher than most modals but below tooltips
                boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.15)',
                borderTopLeftRadius: '10px',
                borderTopRightRadius: '10px',
                width: { sm: 'calc(100vw - 224px - 16px)' } // Match table width: full width minus sidebar+padding (224px) and right padding (16px)
            }}
        >
            {/* Left side - Selection count */}
            <Typography
                sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#fff'
                }}
            >
                {selectionText}
            </Typography>

            {/* Right side - Action text and close icon */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Vertical line before action text */}
                <Box
                    sx={{
                        height: 25,
                        width: '1px',
                        backgroundColor: '#494949',
                    }}
                />
                <Typography
                    onClick={handleActionClick}
                    sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#fff',
                        cursor: 'pointer',
                        marginLeft: 2, // More space after the vertical line
                        '&:hover': {
                            opacity: 0.8
                        }
                    }}
                >
                    {actionText}
                </Typography>
                {/* Vertical line after action text */}
                <Box
                    sx={{
                        height: 25,
                        width: '1px',
                        backgroundColor: '#494949',
                        marginLeft: 2, // More space after the text
                    }}
                />
                <IconButton
                    onClick={handleClose}
                    size="small"
                    sx={{
                        color: '#cccccc',
                        padding: '4px',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                        }
                    }}
                >
                    <CloseOutlinedIcon sx={{ fontSize: '18px' }} />
                </IconButton>
            </Box>
        </Box>
    );
};

export default TeamSelectionBottomBar;

